//SelectUserDialog
//***********************************************************************************************************************
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { Dialog } from "./dialog";
import { Translations } from "ui/Scripts/source/models/translations";
import { IUserListItem, UserListItem } from "../../models/user/userListItem";
import { IEndlessList } from "ui/Scripts/source/models/common/endlessList";
import { DialogButtonType } from "ui/Scripts/source/models/enums";
import { AvatarImage } from "ui/Scripts/source/components/framework/avatarImage";
import { ICompanyMenuItem } from "ui/Scripts/source/models/company/companyMenuItem";
import { Dropdown } from "ui/Scripts/source/components/framework/dropdown";
import { CheckBox } from "ui/Scripts/source/components/framework/checkbox";

//UserListLine
export interface IUserListLineProp {
    item: IUserListItem;
    selected: boolean;
    checked?: boolean;
    multiSelect?: boolean;
    onClick: (id: string) => void;
    onCheckboxChange?: (id: string, checked: boolean) => void;
}

export class UserListLine extends React.Component<IUserListLineProp, {}> {
    handleClick = (e) => {
        const props = this.props;
        e.preventDefault();
        e.stopPropagation();
        props.onClick(props.item.id);
    };

    handleCheckboxChange = (checked: boolean) => {
        const props = this.props;
        if (!props.item || !props.item.id) return;
        props.onCheckboxChange(props.item.id, checked);
    };

    render() {
        const props = this.props;
        const item = props.item;
        return (
            <div className={"line" + (props.selected ? " selected" : "")} onClick={this.handleClick}>
                <div className="row">
                    <div className={"col col-12"}>
                        {!!props.multiSelect && !!props.onCheckboxChange &&
                            <CheckBox
                                title=""
                                classes="checkbox large"
                                inline={true}
                                checked={props.checked ?? false}
                                onClickBoolean={this.handleCheckboxChange}
                            />
                        }
                        <AvatarImage
                            classes="leftDataCont"
                            name={item.name}
                            image={item.picture}
                        />
                        <div className={"dataCont rightAvatar" + (props.onCheckboxChange ? " withCheckBox" : "")}>
                            <div className="singleLine">{item.name}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

//SelectUserDialog
export interface ISelectUserDialogProps {
    classes?: string;
    title: string;
    companies?: ICompanyMenuItem[];
    multiSelect?: boolean;
    loadUsers: (page: number, companyId?: string) => Promise<IEndlessList<UserListItem>>;
    onOk: (users: IUserListItem[], companyId? :string) => void;
    onCancel: () => void;
}

export interface ISelectUserDialogState {
    hasBeenLoaded: boolean;
    company: ICompanyMenuItem;
    users: IUserListItem[];
    page: number;
    hasMore: boolean;
    isLoading: boolean;
    selectedId: string;
    checkedIds: string[];
}

export class SelectUserDialog extends React.Component<ISelectUserDialogProps, ISelectUserDialogState> {
    private containerDiv: HTMLDivElement;
    private listDiv: HTMLDivElement;

    constructor(props) {
        super(props);
        this.state = {
            hasBeenLoaded: false,
            users: [],
            selectedId: null,
            checkedIds: [],
            page: 1,
            isLoading: false,
            hasMore: true,
            company: props.companies && props.companies.length > 0 ? props.companies[0] : null
        };
    }

    loadUsers = async (page: number, reset: boolean = false) => {
        const props = this.props;
        const state = this.state;
        const userList = await props.loadUsers(page, state.company ? state.company.id : null);
        if (!userList) return;
        let users = userList.items.slice(0);
        if (!reset) {
            const oldIds = {};
            for (let j = 0; j < this.state.users.length; j++) {
                oldIds[this.state.users[j].id] = true;
            }
            const newItems = userList.items.filter(i => Object.prototype.hasOwnProperty.call(oldIds, i.id) ? false : (oldIds[i.id] = true));
            users = [...this.state.users, ...newItems];
        }
        this.setState({
            hasBeenLoaded: true,
            users,
            page: userList.page,
            hasMore: userList.hasMore
        });
    };
        
    handleScrollSub = Base.debounce((obj: SelectUserDialog) => {
        if (obj.state.isLoading || !obj.state.hasMore) return;
        if (obj.listDiv.offsetHeight - (obj.containerDiv.clientHeight + obj.containerDiv.scrollTop) < 5) {
            obj.loadUsers(obj.state.page + 1);
        }
    }, 100);

    handleScroll = (event) => {
        this.handleScrollSub(this);
    };

    handleLineClick = (id: string) => {
        const props = this.props;
        const state = this.state;
        if (!id) return;
        const user = state.users.find(i => i.id === id);
        if (!user) return;
        this.setState({
            selectedId: id
        });
        if (props.multiSelect) return;
        props.onOk([user], state.company?.id);
    };

    handleCheckboxChange = (id: string, checked: boolean) => {
        const state = this.state;
        if (!id) return;
        const checkedIds = state.checkedIds.filter(i => i !== id);
        if (checked) {
            checkedIds.push(id);
        }
        this.setState({
            checkedIds
        });
    };

    handleCompanyChange = (companyId: string) => {
        const props = this.props;
        const company = props.companies.find(i => i.id === companyId);
        if (!company) return;
        this.setState({
            company
        }, () => this.loadUsers(this.state.page, true));
    };

    componentDidMount(): void {
        this.containerDiv.addEventListener("scroll", this.handleScroll);
        this.loadUsers(1);
    };

    componentWillUnmount(): void {
        this.containerDiv.removeEventListener("scroll", this.handleScroll);
    }

    handleOkClick = () => {
        const props = this.props;
        const state = this.state;
        const selectedIds = Base.getSelectedIds(state.selectedId, state.checkedIds);
        props.onOk(state.users.filter(i => selectedIds.indexOf(i.id) > -1), state.company?.id);
    };

    handleCancelClick = () => {
        this.props.onCancel();
    };

    render() {
        const props = this.props;
        const state = this.state;
        const showCompaniesDropdown = props.companies && props.companies.length > 1;
        return (
            <Dialog
                classes={"selectUser" + (props.classes ? " " + props.classes : "") + (state.hasBeenLoaded ? "" : " invisible")}
                title={this.props.title}
                body={
                    <div>
                        {showCompaniesDropdown &&
                            <div className="form-group">
                                <label className="control-label">{Translations.SelectCompany}</label>
                                <Dropdown
                                    selectedTitle={<span>{state.company.name}</span>}
                                    selectMode={true}
                                    tooltip={Translations.SelectCompany}
                                    actions={props.companies.map((company) => {
                                        return {
                                            title: <div className={"col col-12 flexCont"}>
                                                {!!company.logo &&
                                                    <div className="iconCont flex">
                                                        <img alt="..." src={company.logo} />
                                                    </div>
                                                }
                                                {!!company.logo &&
                                                    <div className="dataCont flex">
                                                        <div className="singleLine">{company.name}</div>
                                                    </div>
                                                }
                                                {!company.logo &&
                                                    <div className="singleLine">{company.name}</div>
                                                }
                                            </div>,
                                            textTitle: company.name,
                                            selected: company.id === state.company.id,
                                            onClick: () => this.handleCompanyChange(company.id)
                                        };
                                    })}
                                />
                            </div>
                        }
                        {showCompaniesDropdown &&
                            <label className="control-label">{Translations.Users}</label>
                        }
                        <div className={"largeDialogContent" + (showCompaniesDropdown ? " withDropDown" : "")} ref={(elem) => { this.containerDiv = elem; }}>
                            <div className="lineContainer" ref={(elem) => { this.listDiv = elem; }}>
                                {state.users.map(item => (
                                    <UserListLine
                                        key={item.id}
                                        item={item}
                                        selected={state.selectedId === item.id}
                                        checked={state.checkedIds.indexOf(item.id) > -1}
                                        multiSelect={props.multiSelect}
                                        onClick={this.handleLineClick}
                                        onCheckboxChange={this.handleCheckboxChange}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                }
                buttons={[
                    { title: Translations.OK, buttonType: DialogButtonType.Primary, onClick: props.multiSelect ? this.handleOkClick : null },
                    { title: Translations.Cancel, buttonType: DialogButtonType.Default, onClick: this.handleCancelClick }
                ]}
                onClose={this.handleCancelClick}/>
        );
    }
}